import { useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle2, ChevronLeft, ChevronRight, UploadCloud } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, Input, LoadingState, Modal, useToast } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { updateQuotePosition, type QuotePosition } from '@/api/quotes.ts'
import { truncateItemName } from './ResolveMissingItemsModal.tsx'

// ─── SearchableSelect (Combobox) ─────────────────────────────────────────────

function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  allowCustom,
}: {
  value: string
  onValueChange: (v: string) => void
  options: Array<{ value: string; label: string; searchText?: string }>
  placeholder?: string
  disabled?: boolean
  allowCustom?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = options.find((o) => o.value === value)
  const displayValue = open ? query : (selected?.label ?? (allowCustom ? value : ''))
  const filtered = options.filter((o) => {
    if (!query) return true
    return (o.searchText ?? o.label).toLowerCase().includes(query.toLowerCase())
  })

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(allowCustom ? value : '') }}
        onChange={(e) => {
          const v = e.currentTarget.value
          setOpen(true)
          setQuery(v)
          if (allowCustom) onValueChange(v)
        }}
        onBlur={() => {
          if (!allowCustom) return
          const match = options.find((o) => o.label.toLowerCase() === query.toLowerCase())
          if (match) onValueChange(match.value)
        }}
        disabled={disabled}
        className="w-full rounded-md border border-gray-300 px-3 py-2 pr-8 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
      />
      {selected && !open && (
        <button
          type="button"
          onClick={() => { onValueChange(''); setQuery(''); inputRef.current?.focus() }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-caption"
          aria-label="Auswahl entfernen"
        >
          ✕
        </button>
      )}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-caption text-gray-400">Keine Treffer</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onValueChange(o.value); setOpen(false); setQuery('') }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-small hover:bg-brand-50 focus:bg-brand-50 focus:outline-none',
                  o.value === value && 'bg-brand-50 font-medium',
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SAP_ITEM_NAME_MAX = 100

function resolveItemCode(position: QuotePosition): string {
  if (position.sapItemCode) return position.sapItemCode
  const desc = (position.description ?? '').trim()
  const m =
    desc.match(/^([A-Z0-9][A-Z0-9._-]{1,40})\s*[–—\-:|]\s+/) ??
    desc.match(/^([A-Z][A-Z0-9._-]{2,40})\s+/)
  return m ? (m[1] ?? '') : ''
}

function resolveItemName(position: QuotePosition): string {
  const desc = (position.description ?? '').trim()
  const code = resolveItemCode(position)
  let stripped = desc
  if (code) {
    const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    stripped = desc.replace(new RegExp(`^${escaped}\\s*[–—\\-:|]?\\s+`), '')
    if (!stripped) stripped = desc
  }
  return truncateItemName(stripped)
}

interface ArticleGroup {
  id: string
  name: string
}

// ─── StepForm ────────────────────────────────────────────────────────────────

export function StepForm({
  position,
  tenantId,
  onCreated,
  onSkip,
}: {
  position: QuotePosition
  tenantId?: string | null
  onCreated: (itemCode: string, itemName: string, salesPrice?: number, purchasePrice?: number) => void
  onSkip: () => void
}) {
  const NO_GROUP = '__none__'
  const [itemCode, setItemCode] = useState(resolveItemCode(position))
  const [itemName, setItemName] = useState(resolveItemName(position))
  const [manufacturer, setManufacturer] = useState('')
  const [salesPrice, setSalesPrice] = useState<string>(
    position.unitPrice ? String(Number(position.unitPrice)) : '',
  )
  const [purchasePrice, setPurchasePrice] = useState<string>('')
  const [unit, setUnit] = useState('Stk')
  const [groupId, setGroupId] = useState<string>(NO_GROUP)
  const [groups, setGroups] = useState<ArticleGroup[]>([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existingLocalOnly, setExistingLocalOnly] = useState<{ id: string; itemCode: string } | null>(null)
  const [pushing, setPushing] = useState(false)
  const [sapManufacturers, setSapManufacturers] = useState<string[]>([])
  const [manufacturersError, setManufacturersError] = useState<string | null>(null)
  const [sapUnits, setSapUnits] = useState<string[]>([])
  const [salesMarginPct, setSalesMarginPct] = useState<number>(30)
  const [fetchingEgis, setFetchingEgis] = useState(false)
  const [egisInfo, setEgisInfo] = useState<string | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    // Reset form when position changes
    setItemCode(resolveItemCode(position))
    setItemName(resolveItemName(position))
    setSalesPrice(position.unitPrice ? String(Number(position.unitPrice)) : '')
    setPurchasePrice('')
    setManufacturer('')
    setError(null)
    setExistingLocalOnly(null)
    setEgisInfo(null)
    setAutoEgisDone(false)
    setAutoGroupDone(false)
  }, [position.id])

  const tenantHeaders = tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : undefined

  useEffect(() => {
    Promise.all([
      apiClient.get('/articles/groups').then((r) => r.data?.data ?? r.data ?? []).catch(() => []),
      apiClient.get('/articles/defaults').then((r) => r.data).catch(() => ({ salesMarginPct: 30 })),
      apiClient.get('/sap/diagnostics', tenantHeaders).then((r) => r.data).catch(() => null),
      apiClient.get('/articles/manufacturers').then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
    ]).then(([groupsRes, defaults, diag, localMfs]) => {
      const sapItemGroups: Array<{ Number?: number; GroupName?: string }> = diag?.itemGroups ?? []
      const validGroups: ArticleGroup[] = sapItemGroups
        .filter((g) => typeof g.Number === 'number' && typeof g.GroupName === 'string' && g.GroupName.length > 0)
        .map((g) => ({ id: String(g.Number), name: g.GroupName! }))
      setGroups(validGroups.length > 0 ? validGroups : (groupsRes as ArticleGroup[]).filter((g) => {
        const ext = g as unknown as { sapGroupCode?: number | null; isTitle?: boolean; isActive?: boolean }
        return !ext.isTitle && ext.isActive !== false && ext.sapGroupCode != null
      }))
      setSalesMarginPct(Number(defaults?.salesMarginPct ?? 30) || 30)

      let preselected: string | null = null
      if (defaults?.defaultItemGroupCode) {
        const match = validGroups.find(
          (g) => String((g as unknown as { sapGroupCode?: number | string }).sapGroupCode ?? g.id) === String(defaults.defaultItemGroupCode),
        )
        if (match) preselected = match.id
      }
      if (!preselected && validGroups.length > 0) preselected = validGroups[0]!.id
      if (preselected) setGroupId(preselected)

      const mfNames = (diag?.manufacturers ?? [])
        .map((m: { ManufacturerName?: string }) => m.ManufacturerName)
        .filter((n: unknown): n is string => typeof n === 'string' && n.length > 0)
      const allMfs = Array.from(new Set<string>([...mfNames, ...(localMfs as string[])])).sort((a, b) => a.localeCompare(b, 'de'))
      setSapManufacturers(allMfs)
      if (diag?.manufacturersError) setManufacturersError(String(diag.manufacturersError))

      const haystack = `${resolveItemName(position)} ${resolveItemCode(position)}`.toLowerCase()
      const aliasMap: Record<string, string[]> = {
        Ubiquiti: ['unifi', 'ubiquiti', 'ubnt'],
        Lenovo: ['lenovo', 'thinkpad', 'thinkcentre', 'thinkbook'],
        HP: ['hewlett-packard', 'probook', 'elitebook', 'hp '],
        Dell: ['dell', 'latitude', 'optiplex', 'precision'],
        Yealink: ['yealink'],
        Microsoft: ['microsoft', 'surface', 'teams'],
        Cisco: ['cisco', 'meraki'],
        Fortinet: ['fortinet', 'fortigate'],
        Eaton: ['eaton'],
        APC: ['apc'],
      }
      function findManufacturer(): string | null {
        for (const mf of allMfs) {
          if (haystack.includes(mf.toLowerCase())) return mf
        }
        for (const [canonical, aliases] of Object.entries(aliasMap)) {
          if (aliases.some((a) => haystack.includes(a))) {
            return allMfs.find((m) => m.toLowerCase() === canonical.toLowerCase()) ?? canonical
          }
        }
        // Fallback: extract item-code prefix before first "-" or "_" (e.g. "DIGITUS" from "DIGITUS-DS55508")
        const code = resolveItemCode(position)
        const prefixM = code.match(/^([A-Z][A-Z0-9]*)[-_]/)
        if (prefixM) {
          const prefix = prefixM[1]!
          const inMfs = allMfs.find((mf) => mf.toLowerCase() === prefix.toLowerCase())
          if (inMfs) return inMfs
          // Custom value only for ≥4 uppercase-only chars — avoids model-number prefixes like "UCG" or "RF"
          if (prefix.length >= 4 && /^[A-Z]+$/.test(prefix)) return prefix
        }
        return null
      }
      const guessed = findManufacturer()
      if (guessed) setManufacturer((prev) => (prev === '' ? guessed : prev))

      const uomNames = (diag?.uomGroups ?? [])
        .map((u: { Code?: string; Name?: string }) => u.Code ?? u.Name)
        .filter((n: unknown): n is string => typeof n === 'string' && n.length > 0)
      setSapUnits(Array.from(new Set(['Stk', 'Std', ...uomNames])).sort())
    }).finally(() => setLoadingGroups(false))
  }, [])

  const [autoEgisDone, setAutoEgisDone] = useState(false)
  useEffect(() => {
    if (autoEgisDone || !itemCode || loadingGroups) return
    setAutoEgisDone(true)
    void fetchEkFromEgis()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCode, loadingGroups, autoEgisDone])

  // Auto-suggest article group from AI when item name is known
  const [autoGroupDone, setAutoGroupDone] = useState(false)
  useEffect(() => {
    if (autoGroupDone || !itemName || loadingGroups || groups.length === 0) return
    setAutoGroupDone(true)
    void (async () => {
      try {
        const res = await apiClient.post('/articles/groups/ai-suggest', {
          articleName: itemName,
          articleCode: itemCode || undefined,
        })
        const suggestion = res.data as { groupCode?: number; groupName?: string }
        if (!suggestion?.groupName) return
        // Find the matching group — try progressively looser matches
        // 1. Exact match
        let match = groups.find(
          (g) => g.name.toLowerCase() === suggestion.groupName!.toLowerCase(),
        )
        // 2. Substring match (SAP name contains suggestion or vice-versa)
        if (!match) {
          const suggLower = suggestion.groupName!.toLowerCase()
          match = groups.find((g) => {
            const gLower = g.name.toLowerCase()
            return gLower.includes(suggLower) || suggLower.includes(gLower)
          })
        }
        // 3. Word-overlap match (≥2 words shared after normalizing punctuation/special chars)
        if (!match) {
          const normalize = (s: string) => s.toLowerCase().replace(/[^a-zäöüß0-9]+/g, ' ').trim()
          const suggWords = new Set(normalize(suggestion.groupName!).split(' ').filter((w) => w.length > 1))
          match = groups.find((g) => {
            const gWords = normalize(g.name).split(' ').filter((w) => w.length > 1)
            return gWords.filter((w) => suggWords.has(w)).length >= 2
          })
        }
        if (match) setGroupId(match.id)
      } catch {
        // Non-critical — user can still pick manually
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName, loadingGroups, groups, autoGroupDone])

  async function fetchEkFromEgis() {
    if (!itemCode.trim()) return
    setFetchingEgis(true)
    setEgisInfo(null)
    try {
      const res = await apiClient.get('/egis/search', { params: { q: itemCode.trim() } })
      const items: Array<{ purchasePrice?: number | null; manufacturer?: string; description?: string; productNumber?: string }> =
        res.data?.items ?? res.data?.data?.items ?? res.data ?? []
      if (!items.length) {
        setEgisInfo(`Keine EGIS-Treffer für „${itemCode}"`)
        return
      }
      const hit = items[0]
      if (typeof hit.purchasePrice === 'number') {
        setPurchasePrice(hit.purchasePrice.toFixed(2))
        if (!salesPrice) {
          const vk = hit.purchasePrice * (1 + salesMarginPct / 100)
          setSalesPrice(vk.toFixed(2))
        }
      }
      if (!manufacturer && hit.manufacturer) setManufacturer(hit.manufacturer)
      setEgisInfo(`EGIS: ${hit.description ?? hit.productNumber} · EK: ${hit.purchasePrice?.toFixed(2) ?? '—'} €`)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setEgisInfo(status === 502 ? 'EGIS nicht erreichbar (502)' : `EGIS-Fehler: ${err instanceof Error ? err.message : 'unbekannt'}`)
    } finally {
      setFetchingEgis(false)
    }
  }

  function calculateVk() {
    const ek = parseFloat(purchasePrice)
    if (!isFinite(ek) || ek <= 0) { setError('Bitte zuerst EK-Preis eingeben'); return }
    setSalesPrice((ek * (1 + salesMarginPct / 100)).toFixed(2))
    setError(null)
  }

  async function recordGroupCorrection(resolvedGroupId: string) {
    const picked = groups.find((g) => g.id === resolvedGroupId)
    if (!picked) return
    try {
      await apiClient.post('/articles/groups/correction', {
        articleName: itemName.trim(),
        articleCode: itemCode.trim() || undefined,
        groupName: picked.name,
      })
    } catch {
      // Non-critical — best effort
    }
  }

  async function pushExistingToSap() {
    if (!existingLocalOnly) return
    setPushing(true)
    setError(null)
    try {
      const patchBody: Record<string, unknown> = { itemName: itemName.trim(), unit }
      if (manufacturer.trim()) patchBody.manufacturer = manufacturer.trim()
      if (salesPrice) patchBody.salesPrice = Number(salesPrice)
      if (purchasePrice) patchBody.purchasePrice = Number(purchasePrice)
      if (groupId && groupId !== NO_GROUP) {
        const picked = groups.find((g) => g.id === groupId)
        const sap = (picked as unknown as { sapGroupCode?: number | string | null })?.sapGroupCode
        if (sap != null) patchBody.sapGroupCode = sap; else patchBody.groupId = groupId
      }
      await apiClient.patch(`/articles/${existingLocalOnly.id}`, patchBody)
      const res = await apiClient.post(`/articles/by-code/${encodeURIComponent(existingLocalOnly.itemCode)}/push`, {}, tenantHeaders)
      if (res.data?.groupFallbackUsed) {
        toast({ title: 'Warengruppe konnte nicht zugewiesen werden – Standardgruppe verwendet', tone: 'warning' })
      }
      if (groupId && groupId !== NO_GROUP) void recordGroupCorrection(groupId)
      onCreated(res.data?.itemCode ?? existingLocalOnly.itemCode, itemName.trim(), salesPrice ? Number(salesPrice) : undefined, Number(purchasePrice) || undefined)
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? (err instanceof Error ? err.message : 'Push fehlgeschlagen'))
    } finally {
      setPushing(false)
    }
  }

  async function submit() {
    setError(null)
    if (!itemCode.trim()) return setError('SAP-Artikelcode ist Pflicht')
    if (!itemName.trim()) return setError('Artikelname ist Pflicht')
    if (itemName.trim().length > SAP_ITEM_NAME_MAX) return setError(`Artikelname zu lang (max. ${SAP_ITEM_NAME_MAX} Zeichen)`)
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = { itemCode: itemCode.trim(), itemName: itemName.trim(), unit }
      if (manufacturer.trim()) body.manufacturer = manufacturer.trim()
      if (salesPrice) body.salesPrice = Number(salesPrice)
      if (purchasePrice) body.purchasePrice = Number(purchasePrice)
      if (groupId && groupId !== NO_GROUP) {
        const picked = groups.find((g) => g.id === groupId)
        const sap = (picked as unknown as { sapGroupCode?: number | string | null })?.sapGroupCode
        if (sap != null) body.sapGroupCode = sap; else body.groupId = groupId
      }
      const res = await apiClient.post('/articles', body, tenantHeaders)
      const created = res.data
      const synced = created?.syncStatus === 'synced' || (Array.isArray(created?.pushedTenants) && created.pushedTenants.length > 0)
      if (!synced) {
        setError('Artikel lokal gespeichert, aber NICHT in SAP angelegt. Bitte SAP-Pflichtfelder prüfen.')
        return
      }
      toast({ title: `„${created.itemName}" in SAP angelegt`, tone: 'success' })
      if (created.groupFallbackUsed) {
        toast({ title: 'Warengruppe konnte nicht zugewiesen werden – Standardgruppe verwendet', tone: 'warning' })
      }
      if (groupId && groupId !== NO_GROUP) void recordGroupCorrection(groupId)
      onCreated(created.itemCode, created.itemName, created.salesPrice, Number(purchasePrice) || undefined)
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string; code?: string; existing?: { id?: string; syncStatus?: string; itemCode?: string; itemName?: string } } } }
      const data = axiosErr?.response?.data
      if (axiosErr?.response?.status === 409 && data?.code === 'ARTICLE_EXISTS' && data?.existing?.id) {
        if (data.existing.syncStatus === 'local_only') {
          setExistingLocalOnly({ id: data.existing.id, itemCode: itemCode.trim() })
          return
        }
        // Article already synced to SAP — assign it directly without re-creating
        toast({ title: `„${data.existing.itemName ?? itemCode.trim()}" bereits in SAP — wird der Position zugewiesen`, tone: 'success' })
        onCreated(data.existing.itemCode ?? itemCode.trim(), data.existing.itemName ?? itemName.trim())
        return
      }
      setError(data?.error ?? (err instanceof Error ? err.message : 'Anlage fehlgeschlagen'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingGroups) return <LoadingState label="SAP-Stammdaten werden geladen…" />

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="SAP-Artikelcode *"
          placeholder="z. B. UCG-MAX"
          value={itemCode}
          onChange={(e) => setItemCode(e.currentTarget.value)}
          disabled={submitting || pushing}
        />
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">
            Artikelname *
            <span className={cn('ml-1 text-caption', itemName.length > SAP_ITEM_NAME_MAX ? 'text-red-600' : 'text-gray-400')}>
              ({itemName.length}/{SAP_ITEM_NAME_MAX})
            </span>
          </label>
          <input
            value={itemName}
            onChange={(e) => setItemName(e.currentTarget.value)}
            disabled={submitting || pushing}
            maxLength={SAP_ITEM_NAME_MAX}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
          />
        </div>
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">
            Hersteller
            {manufacturersError && <span className="ml-1 text-caption text-red-600">· Abruf fehlgeschlagen</span>}
          </label>
          <SearchableSelect
            value={manufacturer}
            onValueChange={setManufacturer}
            placeholder="auswählen oder eingeben"
            disabled={submitting || pushing}
            allowCustom
            options={sapManufacturers.map((m) => ({ value: m, label: m }))}
          />
        </div>
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Einheit</label>
          <SearchableSelect
            value={unit}
            onValueChange={setUnit}
            placeholder="z.B. Stk"
            disabled={submitting || pushing}
            allowCustom
            options={sapUnits.map((u) => ({ value: u, label: u }))}
          />
        </div>
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">EK-Preis (€)</label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.01"
              className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.currentTarget.value)}
              disabled={submitting || pushing}
            />
            <Button variant="secondary" size="sm" onClick={fetchEkFromEgis} loading={fetchingEgis} disabled={submitting || pushing || fetchingEgis}>
              Aus EGIS
            </Button>
          </div>
          {egisInfo && <p className="text-caption text-gray-500 mt-1">{egisInfo}</p>}
        </div>
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">
            VK-Preis (€) <span className="text-caption text-gray-400">Marge {salesMarginPct}%</span>
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.01"
              className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
              value={salesPrice}
              onChange={(e) => setSalesPrice(e.currentTarget.value)}
              disabled={submitting || pushing}
            />
            <Button variant="secondary" size="sm" onClick={calculateVk} disabled={submitting || pushing}>
              Kalkulieren
            </Button>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-small font-medium text-gray-700 mb-1 block">
            Warengruppe (SAP) <span className="text-caption text-gray-400">({groups.length} verfügbar)</span>
          </label>
          <SearchableSelect
            value={groupId === NO_GROUP ? '' : groupId}
            onValueChange={(v) => setGroupId(v || NO_GROUP)}
            placeholder="Gruppe wählen — tippen zum Filtern"
            disabled={submitting || pushing}
            options={groups.map((g) => {
              const code = (g as unknown as { sapGroupCode?: number | string }).sapGroupCode
              return { value: g.id, label: code != null ? `${code} – ${g.name}` : g.name, searchText: `${code ?? ''} ${g.name}` }
            })}
          />
        </div>
      </div>

      {existingLocalOnly && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
          <p className="text-small text-amber-900">
            Artikel <span className="font-mono">{existingLocalOnly.itemCode}</span> existiert lokal, ist aber noch nicht in SAP.
            Beim Klick werden die Formular-Werte übernommen und der Artikel an SAP übergeben.
          </p>
          <div className="flex gap-2">
            <Button variant="primary" size="sm" leadingIcon={<UploadCloud className="w-4 h-4" />} onClick={pushExistingToSap} loading={pushing} disabled={pushing}>
              An SAP pushen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExistingLocalOnly(null)} disabled={pushing}>Abbrechen</Button>
          </div>
        </div>
      )}

      {error && <p className="text-small text-red-700">{error}</p>}

      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <Button variant="ghost" size="sm" onClick={onSkip} disabled={submitting || pushing}>
          Überspringen
        </Button>
        {!existingLocalOnly && (
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<CheckCircle2 className="w-4 h-4" />}
            onClick={submit}
            loading={submitting}
            disabled={submitting || pushing}
          >
            Artikel anlegen & weiter
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Public component ─────────────────────────────────────────────────────────

interface SapArticleCreationWizardProps {
  open: boolean
  quoteId: string
  positions: QuotePosition[]
  /** The SAP mandant this quote belongs to — used to scope article creation to the right company. */
  tenantId?: string | null
  onClose: () => void
  onAllResolved: () => void
  onPositionUpdated: (position: QuotePosition) => void
}

export function SapArticleCreationWizard({
  open,
  quoteId,
  positions,
  tenantId,
  onClose,
  onAllResolved,
  onPositionUpdated,
}: SapArticleCreationWizardProps) {
  const pending = useMemo(
    () => positions.filter((p) => p.kind !== 'note' && p.kind !== 'subtotal' && !p.sapItemCode),
    [positions],
  )

  const [step, setStep] = useState(0)
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  useEffect(() => {
    if (open) { setStep(0); setResolvedIds(new Set()) }
  }, [open])

  const remaining = useMemo(
    () => pending.filter((p) => !resolvedIds.has(p.id)),
    [pending, resolvedIds],
  )

  const clampedStep = Math.min(step, remaining.length - 1)
  const current = remaining[clampedStep]

  useEffect(() => {
    if (open && remaining.length === 0) onAllResolved()
  }, [open, remaining.length, onAllResolved])

  async function handleCreated(itemCode: string, _itemName: string, salesPrice?: number) {
    if (!current) return
    try {
      const patch: Record<string, unknown> = { sapItemCode: itemCode }
      if (!current.unitPrice && salesPrice && salesPrice > 0) patch.unitPrice = salesPrice
      const updated = await updateQuotePosition(quoteId, current.id, patch)
      onPositionUpdated(updated)
      setResolvedIds((prev) => { const n = new Set(prev); n.add(current.id); return n })
    } catch (err) {
      toast({ title: `Zuweisung fehlgeschlagen: ${err instanceof Error ? err.message : 'Fehler'}`, tone: 'danger' })
    }
  }

  function handleSkip() {
    if (!current) return
    setResolvedIds((prev) => { const n = new Set(prev); n.add(current.id); return n })
    toast({ title: `„${current.description?.slice(0, 40)}" übersprungen`, tone: 'warning' })
  }

  if (!current && remaining.length === 0 && resolvedIds.size > 0) {
    return (
      <Modal
        open={open}
        onOpenChange={(o) => { if (!o) onClose() }}
        title="Artikel-Assistent abgeschlossen"
        size="md"
        footer={
          <Button variant="primary" onClick={onClose}>
            Schließen & SAP-Übergabe starten
          </Button>
        }
      >
        <div className="p-6 text-center space-y-2">
          <CheckCircle2 className="mx-auto w-10 h-10 text-emerald-500" />
          <p className="text-body font-medium text-gray-900">Alle Artikel wurden angelegt</p>
          <p className="text-small text-gray-500">
            {resolvedIds.size} Artikel verarbeitet. Du kannst jetzt die SAP-Übergabe starten.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title="Artikel-Assistent: SAP-Artikelanlage"
      description={`Schritt ${clampedStep + 1} von ${remaining.length} — Artikel für jede Position anlegen, dann SAP-Übergabe starten.`}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<ChevronLeft className="w-4 h-4" />}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={clampedStep === 0}
          >
            Zurück
          </Button>
          <span className="text-caption text-gray-400">
            {remaining.length - clampedStep - 1} weitere Position(en) ausstehend
          </span>
          <Button
            variant="secondary"
            size="sm"
            trailingIcon={<ChevronRight className="w-4 h-4" />}
            onClick={() => setStep((s) => Math.min(remaining.length - 1, s + 1))}
            disabled={clampedStep >= remaining.length - 1}
          >
            Weiter
          </Button>
        </div>
      }
    >
      <div className="px-5 py-4 space-y-4 max-h-[75vh] overflow-y-auto">
        {/* Progress bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-brand-500 transition-all duration-300"
              style={{ width: `${pending.length > 0 ? (resolvedIds.size / pending.length) * 100 : 0}%` }}
            />
          </div>
          <span className="text-caption text-gray-500 whitespace-nowrap">
            {resolvedIds.size}/{pending.length}
          </span>
        </div>

        {current && (
          <>
            {/* Position info */}
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-body font-medium text-gray-900">{current.description}</p>
              <p className="text-caption text-gray-500">
                Menge: {Number(current.quantity)}
                {current.unitPrice ? ` · Einzelpreis: ${Number(current.unitPrice).toFixed(2)} €` : ''}
                {' · '}{current.kind === 'service' ? 'Dienstleistung' : current.kind === 'subtotal' ? 'Zwischensumme' : 'Artikel'}
              </p>
            </div>

            <StepForm
              key={current.id}
              position={current}
              tenantId={tenantId}
              onCreated={handleCreated}
              onSkip={handleSkip}
            />
          </>
        )}
      </div>
    </Modal>
  )
}
